import { useState, useMemo, useEffect } from 'react';
import MainLayout from '../../components/MainLayout';
import { FlowForm, FlowFormLeaveContext, List, ListItem, ButtonBar, ButtonBarGroup, useProgressModal, useFlowFormGlobal } from 't-lib-ui/structure';
import { TextButton, InfiniteLoader, Note, DropDownSelector, DropDownSelectorValue, Input } from 't-lib-ui/widgets';
import { useProgressChangeInput } from 't-lib-ui/connector';
import { debounce1 } from 't-lib-ui/helpers';
import { refreshIcon, downloadIcon, flashIcon } from '../../components/icons';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import AdminHeader from '../../components/admin/AdminHeader';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { useGetAdminFeedList, FeedListType } from '../../connector/api';
import { RestAdminFeed } from '../../schema-copy/api-schema';
import { QueryStatus } from 't-lib-ui/connector';
import { formatCompactDate } from '../../utils/formatting';
import { usePreferences } from '../../connector/preferences';
import { useContext } from 'react';
import { RouterContext, goTo } from 't-lib-ui/router';

const FEEDS_TO_SHOW = 25;

interface RenderFeedProps {
  feed: RestAdminFeed;
}

const RenderFeed: React.FC<RenderFeedProps> = ({ feed }) => {
  const statusColor = feed.feedStatus == 'ACTIVE' ? 'var(--success-color)' : 'var(--error-color)';
  const statusText = feed.feedStatus;
  const lastPostDate = formatCompactDate(feed.lastNewPostPubDate);
  const createdDate = formatCompactDate(feed.createdAt);

  // Determine icon color based on feed status
  const isBlocked = feed.feedStatus == 'BLOCK';
  const isBad = feed.feedStatus == 'BAD';
  const iconColor = isBlocked ? 'var(--error-color)' : (isBad ? 'var(--error-color)' : (feed.hasCatalog ? 'var(--dr-link-blue)' : 'var(--success-color)'));

  // Render appropriate icon based on feed state
  const renderIcon = () => {
    if (isBlocked) {
      // Red X for blocked feeds
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: 'var(--error-color)' }}>
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
      );
    }

    if (feed.hasCatalog) {
      // Globe for published feeds (blue if ACTIVE, red if BAD)
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      );
    }

    if (feed.manuallyVerified) {
      // Green checkmark for verified but not published (green if ACTIVE, red if BAD)
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M20 6L9 17l-5-5"/>
        </svg>
      );
    }

    // Question mark for unverified
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: 'var(--gray-500)' }}>
        <circle cx="12" cy="12" r="10"/>
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    );
  };

  return (
    <ListItem focussable="flow" href={`/a/admin/feed/${feed.id}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {renderIcon()}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500 }}>{feed.shortTitle || feed.title || 'Untitled Feed'}</div>
          <div style={{ fontSize: '0.85em', color: 'var(--gray-400)', display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '4px' }}>
            <span style={{ color: statusColor, whiteSpace: 'nowrap' }}>
              {statusText}
              {feed.errorType && ` (${feed.errorType})`}
            </span>
            <span style={{ whiteSpace: 'nowrap' }}>•</span>
            <span style={{ whiteSpace: 'nowrap' }}>{feed.subscriberCount || 0} sub{feed.subscriberCount == 1 ? '' : 's'}</span>
            {lastPostDate && (
              <>
                <span style={{ whiteSpace: 'nowrap' }}>•</span>
                <span style={{ whiteSpace: 'nowrap' }}>last post: {lastPostDate}</span>
              </>
            )}
            {createdDate && (
              <>
                <span style={{ whiteSpace: 'nowrap' }}>•</span>
                <span style={{ whiteSpace: 'nowrap' }}>since: {createdDate}</span>
              </>
            )}
            {feed.hasCatalog ? (
              <>
                <span style={{ whiteSpace: 'nowrap' }}>•</span>
                <span style={{ color: 'var(--dr-link-blue)', whiteSpace: 'nowrap' }}>Published</span>
              </>
            ) : feed.manuallyVerified ? (
              <>
                <span style={{ whiteSpace: 'nowrap' }}>•</span>
                <span style={{ color: 'var(--success-color)', whiteSpace: 'nowrap' }}>Verified</span>
              </>
            ) : (
              <>
                <span style={{ whiteSpace: 'nowrap' }}>•</span>
                <span style={{ color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>Unverified</span>
              </>
            )}
          </div>
        </div>
      </div>
    </ListItem>
  );
};

const AdminFeedsPage: React.FC = () => {
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [preferences, updatePreferences] = usePreferences();
  const routerCtx = useContext(RouterContext);
  const [selectedList, setSelectedList] = useState<FeedListType>(preferences.adminFeedListType || 'unverified');
  const [searchName, setSearchName] = useState<string>(preferences.adminFeedSearchName || '');
  const progressModal = useProgressModal();

  useFlowFormGlobal();

  // Load preferences on mount
  useEffect(() => {
    if (preferences.adminFeedListType) {
      setSelectedList(preferences.adminFeedListType);
    }
    if (preferences.adminFeedSearchName) {
      setSearchName(preferences.adminFeedSearchName);
    }
  }, [preferences.adminFeedListType, preferences.adminFeedSearchName]);

  const { chunks, status, hasMore, loadMore, refresh, changeInput } = useGetAdminFeedList(
    FEEDS_TO_SHOW,
    { listType: selectedList, name: searchName || undefined },
    adminSecret || ''
  );
  const progressChangeInput = useProgressChangeInput(progressModal, changeInput);

  const feedCount = useMemo(
    () => chunks?.reduce((sum, chunk) => sum + chunk.items.length, 0),
    [chunks]
  );

  const handleListChange = ({ id }: DropDownSelectorValue) => {
    const newListType = id as FeedListType;
    setSelectedList(newListType);
    updatePreferences({ adminFeedListType: newListType });
    progressChangeInput({ listType: newListType, name: searchName || undefined }, 'Loading feeds...', 'Loaded', 'listType');
  };

  const debouncedSearch = useMemo(
    () => debounce1(1, (value: string) => {
      changeInput({ listType: selectedList, name: value || undefined });
    }, 600),
    [changeInput, selectedList]
  );

  const handleSearchChange = (value: string) => {
    setSearchName(value);
    updatePreferences({ adminFeedSearchName: value });
    debouncedSearch(value);
  };

  const successful = status == QueryStatus.Success && chunks;

  function RenderChunk({ chunk }: { chunk: RestAdminFeed[] }) {
    return chunk.map((feed) => <RenderFeed key={feed.id} feed={feed} />);
  }

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
        <MainLayout
          selectedFeed="admin-feeds"
          right={({ onExpand }) => (
            <FlowForm main={true} className='full-margin'>
              <AdminHeader title="Feed Admin" onExpand={onExpand} />
              <ButtonBar size="small">
                <TextButton
                  label="Refresh"
                  className="refresh"
                  onClick={() => refresh(true)}
                  focussable="flow"
                  icon={refreshIcon}
                />
                <TextButton
                  label="Import"
                  onClick={() => goTo(routerCtx, '/a/admin/feed/import')}
                  focussable="flow"
                  icon={downloadIcon}
                />
                <TextButton
                  label="Auto-Add"
                  onClick={() => goTo(routerCtx, '/a/admin/auto-feeds')}
                  focussable="flow"
                  icon={flashIcon}
                />
                <ButtonBarGroup groupName="List:">
                  <DropDownSelector
                    focusId="listType"
                    value={selectedList}
                    selections={[
                      { text: 'New Feeds', id: 'new', searchTextLc: 'new' },
                      { text: 'Unverified', id: 'unverified', searchTextLc: 'unverified' },
                      { text: 'Popular', id: 'popular', searchTextLc: 'popular' },
                      { text: 'Problems', id: 'problem', searchTextLc: 'problem' },
                      { text: 'Bad Feeds', id: 'bad', searchTextLc: 'bad' },
                    ]}
                    onSelect={handleListChange}
                    focussable='flow'
                  />
                </ButtonBarGroup>
                <ButtonBarGroup groupName="Filter:">
                  <Input
                    type="text"
                    placeholder="Search by name..."
                    value={searchName}
                    onInput={handleSearchChange}
                    borderless={false}
                    layout="singleCol"
                  />
                </ButtonBarGroup>
              </ButtonBar>

              <List>
                {chunks && chunks.flatMap((chunk) => RenderChunk({ chunk: chunk.items }))}
                {!hasMore && !!feedCount && (
                  <Note type="tiny">
                    Found {feedCount} feed{feedCount == 1 ? '' : 's'}.
                  </Note>
                )}
                {successful && !chunks?.[0].items?.length && (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                    No feeds found in this list.
                  </div>
                )}
                {status == QueryStatus.Error && (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--error-color)' }}>
                    Error loading feeds!
                  </div>
                )}
                <div style={{ textAlign: 'center' }}>
                  <InfiniteLoader active={hasMore} onActivation={loadMore} />
                </div>
              </List>
            </FlowForm>
          )}
          startWithLeftCollapsed={false}
        />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminFeedsPage;
